import moment from "moment-timezone";


export const reportDateFormatter = (date) => {
  if (!date || !(date instanceof Date)) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const milliseconds = String(date.getMilliseconds()).padStart(3, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
};


export const formatReportDate = (dateString, isEndDate = false) => {
  if (!dateString) return "";

  if (dateString instanceof Date) {
    return reportDateFormatter(dateString);
  }

  if (
    typeof dateString === "string" &&
    dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}$/)
  ) {
    return dateString;
  }

  const todayStr = getLocalDateOffset(0);

  if (
    typeof dateString === "string" &&
    dateString.match(/^\d{4}-\d{2}-\d{2}$/)
  ) {
    if (dateString === todayStr && isEndDate) {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      return `${dateString} ${hours}:${minutes}:${seconds}.000`;
    }
    const time = isEndDate ? "23:59:59.000" : "00:00:00.000";
    return `${dateString} ${time}`;
  }

  if (
    typeof dateString === "string" &&
    dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)
  ) {
    const milliseconds = "000";
    return `${dateString}.${milliseconds}`;
  }

  if (
    typeof dateString === "string" &&
    dateString.match(/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/)
  ) {
    const [datePart, timePart] = dateString.split(" ");
    const [day, month, year] = datePart.split("-");
    const milliseconds = "000";
    return `${year}-${month}-${day} ${timePart}.${milliseconds}`;
  }

  try {
    const parsed = moment(dateString);
    if (parsed.isValid()) {
      const formattedDate = parsed.format("YYYY-MM-DD");
      if (formattedDate === todayStr && isEndDate) {
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");
        return `${formattedDate} ${hours}:${minutes}:${seconds}.000`;
      }
      const time = isEndDate ? "23:59:59.000" : "00:00:00.000";
      return `${formattedDate} ${time}`;
    }
  } catch (error) {
    console.warn("Unable to parse date string:", dateString);
  }

  return "";
};

export const reportDateToISO = (dateString) => {
  if (!dateString) return "";

  try {
    const parsed = moment(dateString, "YYYY-MM-DD HH:mm:ss.SSS", true);
    if (parsed.isValid()) {
      return parsed.toISOString();
    }
  } catch (error) {
    console.warn("Unable to convert report date to ISO:", dateString);
  }

  return "";
};

export const formatReportDateForDisplay = (dateString, includeTime = true) => {
  if (!dateString) return "-";

  try {
    const parsed = moment(dateString, "YYYY-MM-DD HH:mm:ss.SSS", true);
    if (parsed.isValid()) {
      const format = includeTime ? "DD MMM YYYY, hh:mm A" : "DD MMM YYYY";
      return parsed.format(format);
    }
  } catch (error) {
    console.warn("Unable to format report date for display:", dateString);
  }

  return "-";
};


export const getReportDateRange = (days = 10) => {
  const endDate = new Date();
  const startDate = new Date();
  const count = Number(days) === 30 || Number(days) === 31 ? 31 : Number(days);
  startDate.setDate(startDate.getDate() - count + 1);
  // Set start date time to 00:00:00.000
  startDate.setHours(0, 0, 0, 0);

  return {
    startDate: formatReportDate(startDate, false),
    endDate: formatReportDate(endDate, false),
  };
};

export const getFirstDateOfMonthForReport = (isEndDate = false) => {
  const currentDate = new Date();
  const firstDate = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  return formatReportDate(firstDate, isEndDate);
};


export const isValidReportDateFormat = (dateString) => {
  if (!dateString || typeof dateString !== "string") return false;
  return /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{3}$/.test(dateString);
};


export const parseReportDate = (dateString) => {
  if (!dateString) return null;

  try {
    const parsed = moment(dateString, "YYYY-MM-DD HH:mm:ss.SSS", true);
    if (parsed.isValid()) {
      return parsed.toDate();
    }
  } catch (error) {
    console.warn("Unable to parse report date:", dateString);
  }

  return null;
};


export const convertDateTimeLocalToReport = (
  datetimeLocalValue,
  isEndDate = false
) => {
  if (!datetimeLocalValue) return "";

  try {
    const todayStr = getLocalDateOffset(0);

    // Handle datetime-local format: YYYY-MM-DDTHH:mm or YYYY-MM-DDTHH:mm:ss
    if (datetimeLocalValue.includes("T")) {
      const [datePart, timePart] = datetimeLocalValue.split("T");
      const timeParts = timePart.split(":");
      const hours = timeParts[0] || "00";
      const minutes = timeParts[1] || "00";
      let seconds = timeParts[2] ? timeParts[2].split(".")[0] : null;

      if (seconds === null) {
        if (datePart === todayStr && isEndDate) {
          const now = new Date();
          seconds = String(now.getSeconds()).padStart(2, "0");
        } else if (isEndDate) {
          seconds = "59";
        } else {
          seconds = "00";
        }
      }

      return `${datePart} ${hours}:${minutes}:${seconds.padStart(2, "0")}.000`;
    }

    // Handle YYYY-MM-DD HH:mm:ss format
    if (datetimeLocalValue.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
      const milliseconds = "000";
      return `${datetimeLocalValue}.${milliseconds}`;
    }

    // Handle YYYY-MM-DD format
    if (datetimeLocalValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      if (datetimeLocalValue === todayStr) {
        if (isEndDate) {
          const now = new Date();
          const hours = String(now.getHours()).padStart(2, "0");
          const minutes = String(now.getMinutes()).padStart(2, "0");
          const seconds = String(now.getSeconds()).padStart(2, "0");
          return `${datetimeLocalValue} ${hours}:${minutes}:${seconds}.000`;
        }
        return `${datetimeLocalValue} 00:00:00.000`;
      }
      const time = isEndDate ? "23:59:59.000" : "00:00:00.000";
      return `${datetimeLocalValue} ${time}`;
    }

    return formatReportDate(datetimeLocalValue, isEndDate);
  } catch (error) {
    console.warn(
      "Unable to convert datetime-local to report format:",
      datetimeLocalValue
    );
    return "";
  }
};


export const convertReportToDateTimeLocal = (reportDate) => {
  if (!reportDate) return "";

  try {
    const parsed = moment(reportDate, [
      "YYYY-MM-DD HH:mm:ss.SSS",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DDTHH:mm",
      "YYYY-MM-DD",
    ]);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DDTHH:mm:ss");
    }
  } catch (error) {
    console.warn(
      "Unable to convert report date to datetime-local:",
      reportDate
    );
  }

  return "";
};


export const getCurrentReportDateTime = () => {
  return reportDateFormatter(new Date());
};

export const formatReportFilterEndDate = (date) => {
  if (!date) return "";
  const now = new Date();
  const currentHours = String(now.getHours()).padStart(2, "0");
  const currentMinutes = String(now.getMinutes()).padStart(2, "0");
  const currentSeconds = String(now.getSeconds()).padStart(2, "0");
  const todayStr = getLocalDateOffset(0);

  let datePart = "";
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    datePart = `${year}-${month}-${day}`;
  } else {
    datePart = String(date).trim().split(/[ T]/)[0];
  }

  if (datePart === todayStr) {
    return `${datePart} ${currentHours}:${currentMinutes}:${currentSeconds}.000`;
  }
  return `${datePart} 23:59:59.000`;
};

export const formatReportFilterStartDate = (date) => {
  if (!date) return "";
  let datePart = "";
  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    datePart = `${year}-${month}-${day}`;
  } else {
    datePart = String(date).trim().split(/[ T]/)[0];
  }
  return `${datePart} 00:00:00.000`;
};

export const addTimeToReportDate = (dateString, isEndDate = false) => {
  if (!dateString) return "";

  const todayStr = getLocalDateOffset(0);
  const rawDate = dateString.split(/[ T]/)[0];

  let time;
  if (isEndDate) {
    if (rawDate === todayStr) {
      const now = new Date();
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      time = `${hours}:${minutes}:${seconds}.000`;
    } else {
      time = "23:59:59.000";
    }
  } else {
    time = "00:00:00.000";
  }

  return `${rawDate} ${time}`;
};

export const addTimeInReportDates = (filterData, offset) => {
  const now = new Date();
  const currentHours = String(now.getHours()).padStart(2, "0");
  const currentMinutes = String(now.getMinutes()).padStart(2, "0");
  const currentSeconds = String(now.getSeconds()).padStart(2, "0");
  const todayStr = getLocalDateOffset(0);

  const getFallbackEndTime = (dateStr) => {
    const rawDate = dateStr?.split(" ")[0];
    if (rawDate === todayStr) {
      return `${currentHours}:${currentMinutes}:${currentSeconds}.000`;
    }
    return "23:59:59.000";
  };

  return {
    ...filterData,
    ...(filterData.startDate
      ? {
          startDate: filterData.startDate.includes(" ")
            ? filterData.startDate
            : `${getDateInTimeZoneForReport(
                offset,
                filterData.startDate?.split(" ")[0]
              )} 00:00:00.000`,
        }
      : {}),
    ...(filterData.endDate
      ? {
          endDate: filterData.endDate.includes(" ")
            ? filterData.endDate
            : `${getDateInTimeZoneForReport(
                offset,
                filterData.endDate?.split(" ")[0]
              )} ${getFallbackEndTime(filterData.endDate)}`,
        }
      : {}),
  };
};

export const getDateInTimeZoneForReport = (offset, dateString) => {
  let [dateYear, dateMonth, dateDay] = dateString.split("-").map((p) => +p);
  let date = new Date();
  date.setUTCFullYear(dateYear);
  date.setUTCMonth(dateMonth - 1);
  date.setUTCDate(dateDay);

  let dateInTimeZone = new Date(date.getTime() + offset * 60000);

  let year = dateInTimeZone.getUTCFullYear();
  let month = String(dateInTimeZone.getUTCMonth() + 1).padStart(2, "0");
  let day = String(dateInTimeZone.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/** Local YYYY-MM-DD for calendar bounds (avoids UTC day shift from toISOString). */
export const getLocalDateOffset = (daysAgo = 0) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * End-date calendar min: later of (today - daysBack) and selected start date.
 * startDate may be datetime-local (T) or report format (space).
 */
export const getReportEndDateMin = (startDate, daysBack = 30) => {
  const minSelectableDate = getLocalDateOffset(daysBack);
  if (!startDate) return minSelectableDate;
  const startDay = String(startDate).split(/[T ]/)[0];
  return startDay > minSelectableDate ? startDay : minSelectableDate;
};
